const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface LoginRequest {
  email: string;
  password: string;
  twoFactorCode?: string;
}

export interface LoginResponse {
  token?: string;
  user?: {
    id: number;
    email: string;
    name: string;
    role: string;
    twoFactorEnabled?: boolean;
  };
  requiresTwoFactor?: boolean;
  message?: string;
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
}

class AuthService {
  private tokenKey = 'chatbot_admin_token';
  private userKey = 'chatbot_admin_user';

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data: LoginResponse = await response.json();

    // If 2FA is required, return without storing credentials
    if (data.requiresTwoFactor) {
      return data;
    }

    // Only store token and user data if login is complete
    if (data.token && data.user) {
      localStorage.setItem(this.tokenKey, data.token);
      localStorage.setItem(this.userKey, JSON.stringify(data.user));
    }

    return data;
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getUser(): User | null {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return token
      ? {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      : {
          'Content-Type': 'application/json',
        };
  }

  async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If unauthorized, clear stored data and redirect to login
    if (response.status === 401 || response.status === 403) {
      this.logout();
      window.location.href = '/login';
    }

    return response;
  }
}

export const authService = new AuthService();
export default authService;